import React, { Component, useEffect, useState } from 'react';
import LoginPage from './LoginPage';
import { Route, BrowserRouter, Routes, useNavigate, useLocation } from "react-router-dom";
import { config } from './Constants';
import Cookies from 'js-cookie'
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from "react-i18next";
import EmailVerificationPage from './EmailVerificationPage';
import SetupFlow from './SetupPages/SetupFlow';
import RegisterPage from './RegisterPage'
import App from './App'
import { logout } from './utils/LoadInformation';
import { TasLoader, checkConnectivity } from './utils/general';
import posthog from 'posthog-js'
import { ToastContainer, toast } from 'react-toastify';

const ResetPasswordPage = React.lazy(() => import('./ResetPassword'));

function loadMetadata(restaurantId, callback, errorCallback) {
	fetch(`${config.url.PUBLIC}/restaurants/${restaurantId}/metadata.json`)
		.then(resp => {
			if (resp.status === 200) resp.json().then(data => {
				if (callback) callback(data)
			})
			else if (errorCallback) errorCallback(resp.status)
		})
		.catch(() => {
			if (errorCallback) errorCallback(0)
		})
}

const noLoginRequiredLocations = ['/login', '/register', '/reset_password', '/verify-email']

// Only same-site, non-login destinations may be restored after login (TAK-11).
function safeRedirectPath(path) {
	if (typeof path !== 'string') return '/'
	if (!path.startsWith('/') || path.startsWith('//')) return '/'
	if (path.startsWith('/login')) return '/'
	return path
}

function AppRoutes({ restaurantId, onLogin, pathRequested, doSetup, refreshMetadata, prebuiltMenu, onLogout, metadata, redirectTo, loginError }) {
	const [firstRender, setFirstRender] = useState(true)
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		setFirstRender(false)
	}, [])

	useEffect(() => {
		if (restaurantId === '' &&
			!noLoginRequiredLocations.some(ele => location.pathname.startsWith(ele))
		) navigate('/login', { state: { from: location.pathname + location.search } })
	}, [restaurantId])

	useEffect(() => {
		if (!metadata) return

		if (metadata.setup !== 'completed') {
			navigate('/setup')
		} else if (redirectTo) {
			navigate(redirectTo)
		} else if (location.pathname.startsWith('/setup')) {
			navigate('/business-info')
		}

	}, [redirectTo, metadata])

	if (firstRender) return <></> // let the navigate apply

	// Where the user wanted to go before being sent to /login (TAK-11).
	const requestedPath = (location.state && typeof location.state.from === 'string') ? location.state.from : null
	const loginReason = (location.state && typeof location.state.reason === 'string') ? location.state.reason : null

	return <Routes>
		<Route exact path="/register" element={<RegisterPage />} /> { /** TODO: check if it needs props */}
		<Route exact path="/reset_password" element={
			<React.Suspense fallback={<TasLoader />}>
				<ResetPasswordPage />
			</React.Suspense>
		} />
		<Route exact path="/login" element={
			<LoginPage
				onLogin={onLogin}
				restaurantId={restaurantId} // TODO: remove?
				redirectPath={requestedPath || pathRequested}
				reason={loginReason}
				loginError={loginError} />
		} />
		<Route exact path="/verify-email/:verificationCode" element={
			<EmailVerificationPage onValidate={doSetup} />
		} />
		<Route exact path="/setup" element={
			<SetupFlow
				restaurantId={restaurantId}
				refreshMetadata={refreshMetadata}
				prebuilt={prebuiltMenu}
				onLogout={onLogout}
				metadata={metadata} />
		} />
		<Route path="/*" element={
			<App
				// restaurantId={restaurantId} // TODO: read from metadata inside App
				onLogout={onLogout}
				onLogin={onLogin}
				pathname={redirectTo || pathRequested}
				// {...props} TODO: check commenting is ok
				search={window.location.search}
				defaultMetadata={metadata} />
		} />
	</Routes >
}

// Initialize PostHog at module scope so the /decide roundtrip starts at script
// evaluation, not after React's first render: the register A/B test
// (register-defer-solutions, TAK-27) needs the flag before rendering the
// signup form, so every bit of decide latency is users staring at a loader.
// Single instance (TAK-13): the init snippet that used to run in
// public/index.html created a second one alongside this npm instance,
// double-firing every $pageview and $autocapture.
//
// Automated runs must stay out of production analytics (TAK-28): the Aug 30
// signup wave was the local e2e suite firing register_success and $pageviews
// into the prod project from the dev server. Playwright (any CDP-driven
// Chromium) sets navigator.webdriver; TEST_RUN in env-config.js covers
// drivers that strip the flag.
//
// Analytics must never take the app down (TAK-27): if init throws (blocked
// storage, extension interference), the app keeps working and RegisterPage
// falls back to the control flow.
try {
	const isTestRun = navigator.webdriver === true || window._env_?.TEST_RUN === true

	posthog.init('phc_1NUABBjtoBfQVsWkE6AxAdMwBMn9FCVFYt3LTOBKpjO', {
		api_host: 'https://eu.posthog.com',
		// $pageview once per SPA route change (posthog-js default, made explicit).
		capture_pageview: !isTestRun,
		// Report uncaught errors and unhandled promise rejections as $exception.
		capture_exceptions: !isTestRun,
		autocapture: !isTestRun,
		// Session replay is enabled on the project — never record test runs.
		disable_session_recording: isTestRun,
	})

	if (isTestRun) {
		// Tag whatever could still slip through, then opt out: posthog-js
		// drops every capture from an opted-out client, including the
		// explicit posthog.capture calls (register_success, login_failed…).
		posthog.register({ test_run: true })
		posthog.opt_out_capturing()
	}
} catch (e) { /* analytics unavailable — non-fatal */ }

function AppRouter() {
	const [metadata, setMetadata] = useState(null)
	const [restaurantId, setRestaurantId] = useState(null)
	const [redirectTo, setRedirectTo] = useState(null)
	const [pathRequested, setPathRequested] = useState(window.location.pathname)
	const [prebuiltMenu, setPrebuiltMenu] = useState(null)
	const [loginError, setLoginError] = useState(null)
	const { i18n } = useTranslation('app');

	useEffect(() => {
		checkConnectivity().then(isConnected => {
			if (!isConnected) toast.error("No internet connection.")
		})
	}, [])

	useEffect(() => {
		if (restaurantId !== null) return

		// Check if the user is already logged in
		fetch(`${config.url.API}/restaurant/id`, {
			method: 'get',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			credentials: "include"
		}).then(resp => {
			if (resp.status === 200) resp.json().then(id => setRestaurantId(id));
			else setRestaurantId('')
		}).catch(e => setRestaurantId(''))
	}, [restaurantId])

	useEffect(() => {
		if (!restaurantId) return

		loadMetadata(restaurantId, (data) => setMetadata(data))
	}, [restaurantId])

	function onLogin(restaurantId, redirectionPath) { // TODO: handle redirectTo better
		const pathToRedirectTo = safeRedirectPath(redirectionPath)

		setRestaurantId(restaurantId)
		setLoginError(null)
		loadMetadata(restaurantId, (data) => {
			setMetadata(data)
			setRedirectTo(pathToRedirectTo)
		}, () => {
			// The login itself succeeded but the account data could not be
			// loaded: without this the button stayed on "Loading…" forever
			// on /login with no feedback. (TAK-11)
			posthog.capture('post_login_metadata_failed')
			// Back to the logged-out state so the form (and the error) render
			// instead of the AppRouter blanking everything while metadata is
			// still null.
			setRestaurantId('')
			setLoginError('metadata-error')
		})
	}

	function refreshMetadata(newMetadata, callback) {
		setMetadata(newMetadata, () => {
			if (callback) callback()
		})
	}

	function onLogout() {
		logout(() => setRestaurantId(''))
	}

	function doSetup(id, lang, prebuilt) {
		i18n.changeLanguage(lang)

		const current = new Date();
		const nextYear = new Date();
		nextYear.setFullYear(current.getFullYear() + 1);

		Cookies.set("lang", lang, { domain: config.cookies.domain, expires: nextYear })
		setRestaurantId(id)
		setPrebuiltMenu(prebuilt)
	}

	// We first attempt to load the user id. Show loader when waiting.
	if (restaurantId === null || (
		restaurantId && metadata === null
	)) {
		return <></>
	}

	return <BrowserRouter>
		<ToastContainer />
		<AppRoutes
			restaurantId={restaurantId}
			onLogin={onLogin}
			pathRequested={pathRequested}
			doSetup={doSetup}
			refreshMetadata={refreshMetadata}
			prebuiltMenu={prebuiltMenu}
			onLogout={onLogout}
			metadata={metadata}
			redirectTo={redirectTo}
			loginError={loginError} />
	</BrowserRouter>
}

// const AppRouterTranslated = withTranslation('app')(AppRouter);

export default AppRouter;